import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Paper, Stack, Switch, Typography } from '@mui/material';
import { useToast } from '../../context/ToastContext';
import { MODULE_META } from '../dashboard/registry/moduleMeta';
import { AddonKey, AddonService, AddonStates } from '../../services/addon.service';
import { ADDONS_CHANGED_EVENT } from './useAddon';

/**
 * Kervolt team only: switch the help and feedback add-ons on or off for every institution at once. Per institution
 * they are ordinary modules (Tenant Provisioning, and the admin's Modules card in Institution Settings).
 */
const PlatformAddonsPage: React.FC = () => {
    const toast = useToast();
    const [states, setStates] = useState<AddonStates | null>(null);
    const [saving, setSaving] = useState<AddonKey | null>(null);

    useEffect(() => {
        AddonService.platformList()
            .then(setStates)
            .catch(error => toast.showApiError(error, 'Failed to load add-ons'));
    }, [toast]);

    const change = async (key: AddonKey, enabled: boolean) => {
        setSaving(key);
        try {
            setStates(await AddonService.platformUpdate(key, enabled));
            window.dispatchEvent(new Event(ADDONS_CHANGED_EVENT));
            toast.showSuccess(`${MODULE_META[key].label} is now ${enabled ? 'on' : 'off'} for every institution.`);
        } catch (error) {
            toast.showApiError(error, 'Failed to change the add-on');
        } finally {
            setSaving(null);
        }
    };

    const row = (key: AddonKey) => (
        <Paper variant="outlined" sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
                <Typography variant="subtitle1">{MODULE_META[key].label}</Typography>
                <Typography variant="body2" color="text.secondary">{MODULE_META[key].blurb}</Typography>
            </Box>
            <Switch
                checked={states?.[key] ?? true}
                disabled={saving !== null}
                onChange={e => change(key, e.target.checked)}
                inputProps={{ 'aria-label': `${MODULE_META[key].label} for every institution` }}
            />
        </Paper>
    );

    return (
        <Box sx={{ p: 3 }} data-help-page="platform.addons.page">
            <Typography variant="h5" sx={{ mb: 1 }}>Add-ons</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Off here hides the add-on in every institution. On here, each institution still needs it in its modules
                (Premium Provisioning), and its admin can switch it off in Institution Settings.
            </Typography>
            {!states ? <CircularProgress size={24} /> : (
                <Stack spacing={1.5} sx={{ maxWidth: 640 }}>
                    {/* One literal data-help per row, so the help key-coverage check can find them. */}
                    <Box data-help="platform.addons.help">{row('help')}</Box>
                    <Box data-help="platform.addons.feedback">{row('feedback')}</Box>
                    <Box data-help="platform.addons.xero">{row('xero')}</Box>
                </Stack>
            )}
        </Box>
    );
};

export default PlatformAddonsPage;
